import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { submitLead } from "../lib/api";
import { SERVICES } from "../data/siteData";

export default function LeadForm({ defaultService = "", city = "", sourcePage = "", dark = false }) {
  const [form, setForm] = useState({
    name: "", phone: "", email: "",
    service: defaultService, city: city, message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitLead({ ...form, source_page: sourcePage || window.location.pathname });
      setDone(true);
      toast.success("Request received — our team will call you within 4 business hours.");
    } catch (err) {
      toast.error("Something went wrong. Please call us directly at +91 73510 10107.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = `w-full rounded-xl border px-4 py-3 text-sm outline-none transition-all focus:ring-2 focus:ring-brand-blue/40 ${
    dark
      ? "border-white/15 bg-white/10 text-white placeholder:text-white/40"
      : "border-brand-blue/15 bg-white/80 text-brand-navy placeholder:text-brand-gray/60"
  }`;

  if (done) {
    return (
      <div data-testid="lead-form-success" className={`rounded-2xl p-8 text-center ${dark ? "glass-dark" : "glass"}`}>
        <CheckCircle2 className="mx-auto mb-3 h-12 w-12 text-brand-blue" />
        <h3 className={`font-display text-xl font-bold ${dark ? "text-white" : "text-brand-navy"}`}>You're on the list.</h3>
        <p className={`mt-2 text-sm ${dark ? "text-white/60" : "text-brand-gray"}`}>
          A senior advisor will reach out within 4 business hours. For anything urgent, call +91 73510 10107.
        </p>
      </div>
    );
  }

  return (
    <form data-testid="lead-form" onSubmit={onSubmit} className={`rounded-2xl p-6 sm:p-8 ${dark ? "glass-dark" : "glass"}`}>
      <div className="grid gap-4 sm:grid-cols-2">
        <input data-testid="lead-form-name" required minLength={2} placeholder="Full name" value={form.name} onChange={set("name")} className={inputCls} />
        <input data-testid="lead-form-phone" required minLength={7} pattern="[0-9+\-\s]{7,15}" placeholder="Phone number" value={form.phone} onChange={set("phone")} className={inputCls} />
        <input data-testid="lead-form-email" required type="email" placeholder="Work email" value={form.email} onChange={set("email")} className={`${inputCls} sm:col-span-2`} />
        <select
          data-testid="lead-form-service"
          required
          value={form.service}
          onChange={set("service")}
          className={`${inputCls} ${dark ? "[&>option]:text-brand-navy" : ""}`}
        >
          <option value="" disabled>Service you're interested in</option>
          {SERVICES.map((s) => (
            <option key={s.slug} value={s.name}>{s.name}</option>
          ))}
          <option value="Business Valuation Report">Business Valuation Report</option>
          <option value="Other">Other / Not sure yet</option>
        </select>
        <input data-testid="lead-form-city" placeholder="City" value={form.city} onChange={set("city")} className={inputCls} />
        <textarea data-testid="lead-form-message" rows={3} placeholder="Tell us briefly about your requirement (optional)" value={form.message} onChange={set("message")} className={`${inputCls} sm:col-span-2`} />
      </div>
      <button
        data-testid="lead-form-submit"
        type="submit"
        disabled={loading}
        className="btn-gradient mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold text-white shadow-lg shadow-brand-blue/25 disabled:opacity-60"
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Book Free Consultation <ArrowRight className="h-4 w-4" /></>}
      </button>
      <p className={`mt-3 text-center text-xs ${dark ? "text-white/40" : "text-brand-gray/70"}`}>
        100% confidential. No spam — a senior advisor calls, not a sales bot.
      </p>
    </form>
  );
}
